import React, { useState } from 'react';
import { Row, Col, Table, Tooltip, Button, InputNumber, Modal, Avatar, Card, Icon } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import Colors from '../themes/Colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from './NumberFormat';
import { calculateValueForPrice, formatNumber, SeparateNumberFormat } from '../services/utils';
import { getMaxExtractableSpots } from '../services/display';

const DisplayViewComponent = ({
  contentsDisplaysSelect, 
  currency,
  calculateDiffValue,
  calculateImpressionsView,
}) => {
  const { t } = useTranslation();

  // Group customContents by displayId to create display-level aggregations
  const getDisplayData = (customContents) => {
    const displayMap = new Map();

    customContents.forEach(content => {
      const displayId = content.displayId;
      
      if (!displayMap.has(displayId)) {
        displayMap.set(displayId, {
          display_id: displayId,
          display_name: content.display_name,
          smart_campaign_cpm: content.smart_campaign_cpm,
          contents: [],
          // Get discount info from first content (discount is per display, same for all contents)
          appliedDiscount: content.appliedDiscount || null,
          spotsPerDay: content.spotsPerDay || 0,
        });
      }
      
      displayMap.get(displayId).contents.push(content);
    });

    // Convert map to array and calculate totals for each display
    const result = Array.from(displayMap.values()).map(display => {
      // Get relationShowDays from first content (same for all contents in display)
      const relationShowDays = (display.contents[0] && display.contents[0].relationShowDays) || 0;
      
      const totals = display.contents.reduce((acc, content) => {
        const currentImpressions = content.promised_shows_update !== undefined 
          ? content.promised_shows_update 
          : calculateImpressionsView(content);
        
        acc.total_pending_spots += currentImpressions;
        acc.total_remaining_spots += calculateDiffValue("subtract", content);
        acc.total_added_spots += calculateDiffValue("add", content);
        acc.total_budget += calculateValueForPrice(content.smart_campaign_cpm, currentImpressions);
        acc.total_spotsPerDay += content.spotsPerDay;
        return acc;
      }, {
        total_pending_spots: 0,
        total_remaining_spots: 0,
        total_added_spots: 0,
        total_budget: 0,
        total_spotsPerDay: 0,
      });

      // Calculate max extractable spots for this display
      const maxExtractable = getMaxExtractableSpots(
        totals.total_pending_spots,
        totals.total_spotsPerDay,
        display.appliedDiscount,
        relationShowDays
      );

      return {
        ...display,
        ...totals,
        maxExtractable,
        relationShowDays,
      };
    });

    return result;
  };

  const columns = [
    {
      title: t('Screen'),
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: t('Pending spots'),
      key: 'total_pending_spots',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={t('Shows the remaining promised spots')}>
            <InputNumber 
              disabled
              type="text"
              formatter={SeparateNumberFormat}
              parser={value => Number(value.replace(/\./g, ''))}
              value={record.total_pending_spots}
              style={{ color: Colors.black }}
              className='text-impression-disabled-important'
            />
          </Tooltip>
          {
            record.appliedDiscount ? (
              <Tooltip 
                title={
                  <div>
                    <Trans 
                      i18nKey="volume discount extraction limit"
                      values={{ 
                        limit: SeparateNumberFormat(record.maxExtractable),
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </div>
                }
              >
                <Icon type="exclamation-circle" style={{ color: Colors.red, fontSize: '16px' }} />
              </Tooltip>
            ) : null
          }
        </div>
      ),
    },
    {
      title: t('Subtracted spots'),
      key: 'total_remaining_spots',
      render: (text, record) => (
          <InputNumber 
            disabled 
            type="text"
            formatter={SeparateNumberFormat}
            parser={value => Number(value.replace(/\./g, ''))}
            value={record.total_remaining_spots}
            style={{ color: Colors.black }}
            className='text-impression-disabled-important'
          />
      ),
    },
    {
      title: t('Added spots'),
      key: 'total_added_spots',
      render: (text, record) => (
          <InputNumber 
            disabled
            type="text"
            formatter={SeparateNumberFormat}
            parser={value => Number(value.replace(/\./g, ''))}
            value={record.total_added_spots}
            style={{ color: Colors.black }}
            className='text-impression-disabled-important'
          />
      ),
    },
    {
      title: t('Budget'),
      key: 'total_budget',
      render: (text, record) => (
        <InputNumber 
          disabled
          value={record.total_budget}
          formatter={value => `$${currency} ${formatNumber(value)}`}
          style={{ color: Colors.black }}
          className='text-impression-disabled-important'
        />
      ),
    },
    {
      title: t('CPM/spot'),
      dataIndex: 'smart_campaign_cpm',
      key: 'cpm',
      render: (cpm) => <NumberFormat value={cpm} currency={currency} />
    },
  ];

  return (
    <div>
      {contentsDisplaysSelect.map(collection => {
        const displayData = getDisplayData(collection.customContents);
        
        return(
          <Card key={collection.key} title={collection.key} headStyle={{ fontWeight: "bolder" }}>
            <Table
              columns={columns}
              dataSource={displayData}
              rowKey="display_id"
              pagination={false}
            />
          </Card>
        )
      })}
    </div>
  );
};

export default DisplayViewComponent;
